import { useMemo, useState } from 'react';
import { PnlReportSummary } from '../starGridTypes';
import { normalizeReportStatus } from '../utils/reportStatus';

/** Sort keys the home view supports. Values match `<option value>` in the sort dropdown. */
export type HomeSortKey = 'recent' | 'oldest' | 'name' | 'status';

export const HOME_SORT_OPTIONS: { value: HomeSortKey; label: string }[] = [
  { value: 'recent', label: 'Most Recent' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'name', label: 'Name (A→Z)' },
  { value: 'status', label: 'Status' }
];

/** Rank statuses so the Status sort is meaningful (Generating first — likely user attention). */
const STATUS_RANK: Record<string, number> = { GENERATING: 0, FAILED: 1, READY: 2, DELETED: 3 };

function cmpTimestampDesc(a?: string | null, b?: string | null): number {
  const ta = a ? new Date(a).getTime() : 0;
  const tb = b ? new Date(b).getTime() : 0;
  return tb - ta;
}

/** Which of the two things the landing page offers the user is doing. */
export type HomeMode = 'choose' | 'modify';

export interface HomeViewState {
  sortKey: HomeSortKey;
  setSortKey: (k: HomeSortKey) => void;
  /** Reports filtered (drop DELETED) + sorted per the current sort key. */
  visibleReports: PnlReportSummary[];
  /** 'choose' shows the two action cards; 'modify' reveals the saved-report list. */
  mode: HomeMode;
  setMode: (m: HomeMode) => void;
}

/** Home-view UI state: sort key selection + derived visible/ordered report list. */
export function useHomeViewState(reports: readonly PnlReportSummary[]): HomeViewState {
  const [sortKey, setSortKey] = useState<HomeSortKey>('recent');
  // The landing page asks which task first; the report list is a step INTO modifying one, not the
  // page itself, so it stays collapsed until asked for.
  const [mode, setMode] = useState<HomeMode>('choose');

  const visibleReports = useMemo<PnlReportSummary[]>(() => {
    const filtered = reports.filter((r) => normalizeReportStatus(r.status) !== 'DELETED');
    const arr = [...filtered];
    switch (sortKey) {
      case 'recent':
        arr.sort((a, b) => cmpTimestampDesc(a.created_at, b.created_at));
        break;
      case 'oldest':
        arr.sort((a, b) => cmpTimestampDesc(a.created_at, b.created_at)).reverse();
        break;
      case 'name':
        arr.sort((a, b) => a.report_name.localeCompare(b.report_name));
        break;
      case 'status':
        arr.sort((a, b) => {
          const rankDiff = (STATUS_RANK[normalizeReportStatus(a.status)] ?? 99) - (STATUS_RANK[normalizeReportStatus(b.status)] ?? 99);
          if (rankDiff !== 0) return rankDiff;
          return cmpTimestampDesc(a.created_at, b.created_at);
        });
        break;
    }
    return arr;
  }, [reports, sortKey]);

  return { sortKey, setSortKey, visibleReports, mode, setMode };
}
